import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import * as yup from 'yup';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import BaseButton from '../../Components/Buttons/BaseButton';
import BaseDialog from '../../Components/Dialogs/BaseDialog';
import TextInput from '../../Components/Inputs/TextInput';
import { SlideUpTransition } from '../../Components/Transitions/Transitions';

import {
  redirectOnAuthFailure,
  extractErrorMessage
} from '../../Utils/Errors/Errors';

import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';
import {
  VerifyPhoneNumberAPI,
  ResendPhoneConfirmationAPI
} from '../../API/UserAPI';
import {
  VerifyPhoneCodeAPI,
  ResendPhoneCodeAPI
} from '../../API/PhoneValidation/PhoneValidationAPI';
import Recaptcha from '../../Components/Captcha/TurnstileManagedCaptcha';
import { useCaptcha } from '../../Hooks/useCaptcha';

import _get from 'lodash/get';
import { logoutUser, updateUser } from '../../Utils/User/Actions';
import { useMediaQuery } from '@material-ui/core';
import { PortalCheck } from '../../Configs/general';

const RESEND_FLAG_SECONDS_DELAY = 120;

const clearInputs = (codeInputs, setCode, codeLength) => {
  setCode(Array(codeLength).fill(''));
  codeInputs.current.forEach(ref => {
    if (ref) {
      ref.value = '';
    }
  });
};

const handleVerificationError = ({
  error,
  setCode,
  codeInputs,
  codeLength,
  setAlert,
  setLoading,
  setCurrentUser
}) => {
  clearInputs(codeInputs, setCode, codeLength);

  if (redirectOnAuthFailure(error, '/', () => logoutUser(setCurrentUser))) {
    return true;
  }

  setAlert({
    type: 'error',
    message: extractErrorMessage(error) || 'Error al verificar el código'
  });
  setLoading(false);
  return false;
};

const PhoneVerificationDialog = props => {
  const {
    open,
    setDialog,
    setAlert,
    title,
    requestCallback,
    successCallback,
    currentPassword = null,
    formEmail = null,
    isAlphanumeric = false,
    otpChannels = null
  } = props;

  const codeLength = useMemo(() => {
    return isAlphanumeric ? 6 : 4;
  }, [isAlphanumeric]);
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);

  const getVerificationCodeSchema = useCallback(() => {
    if (isAlphanumeric) {
      return yup.object({
        code: yup
          .array()
          .of(
            yup
              .string()
              .matches(/^[A-Za-z0-9]$/, 'Solo letras y números')
              .required('Requerido')
          )
          .length(codeLength, `El código debe tener ${codeLength} caracteres`)
      });
    }
    return yup.object({
      code: yup
        .array()
        .of(
          yup
            .number()
            .positive()
            .integer()
            .min(0)
            .max(9)
        )
        .min(codeLength)
        .max(codeLength)
    });
  }, [isAlphanumeric, codeLength]);

  const verificationCodeSchema = getVerificationCodeSchema();
  const [code, setCode] = useState(Array(codeLength).fill(''));
  const [canSubmit, setCanSubmit] = useState(false);
  const [counter, setCounter] = useState(RESEND_FLAG_SECONDS_DELAY);
  const [canResend, setCanResend] = useState(false);
  const [timer, setTimer] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    captchaRef: recaptchaRef,
    handlers: captchaHandlers,
    captchaToken: captcha
  } = useCaptcha();
  const inputRef = useRef(null);

  const phone = _get(currentUser, 'phone', '');
  const phoneCountryCode = _get(currentUser, 'phoneCountryCode', '');
  const userEmail = formEmail || _get(currentUser, 'email', '');
  const codeInputs = useRef([]);

  const classes = useStyles({ codeLength });

  const authToken = _get(currentUser, 'token');

  const confirmPhone = useCallback(
    async event => {
      event.preventDefault();

      if (!canSubmit || loading) {
        return false;
      }

      try {
        setLoading(true);
        const fullCode = code.join('');

        if (isAlphanumeric && codeLength === 6) {
          const recaptchaToken = captcha;
          captchaHandlers.reset();
          const response = await VerifyPhoneCodeAPI({
            code: fullCode,
            email: userEmail,
            passwordChangeRequired: true,
            recaptchaToken
          });

          if (response.success) {
            setAlert({
              type: 'success',
              message: 'Tu codigo OTP fue confirmado exitosamente'
            });
            setLoading(false);
            setDialog(false);
            successCallback(response);
            return;
          }

          setCode(Array(codeLength).fill(''));
          codeInputs.current.forEach(ref => {
            if (ref) {
              ref.value = '';
            }
          });

          setAlert({
            type: 'error',
            message: extractErrorMessage(response).message || 'Código inválido'
          });
          setLoading(false);
          return;
        }

        const response = await VerifyPhoneNumberAPI(authToken, fullCode);

        if (response.success) {
          if (requestCallback) {
            const updateResponse = await requestCallback(currentPassword);
            if (!updateResponse.success) {
              clearInputs(codeInputs, setCode, codeLength);
              setAlert({
                type: 'error',
                message: extractErrorMessage(updateResponse).message
              });
              setLoading(false);
              return false;
            }
          }

          setAlert({
            type: 'success',
            message: 'Tu número celular fue confirmado exitosamente'
          });

          if (successCallback) {
            successCallback(response);
          }

          setLoading(false);
          setDialog(false);

          const { token: newToken, appToken: newAppToken } = response.data.data;
          if (newToken && newAppToken) {
            updateUser(setCurrentUser, newToken, newAppToken);
          }
          return true;
        }

        clearInputs(codeInputs, setCode, codeLength);
        setAlert({
          type: 'error',
          message: extractErrorMessage(response).message || 'Código inválido'
        });
        setLoading(false);
        return false;
      } catch (error) {
        return handleVerificationError({
          error,
          setCode,
          codeInputs,
          codeLength,
          setAlert,
          setLoading,
          setCurrentUser
        });
      }
    },
    [
      setAlert,
      setDialog,
      canSubmit,
      setCurrentUser,
      authToken,
      code,
      loading,
      requestCallback,
      successCallback,
      currentPassword,
      codeLength,
      isAlphanumeric,
      userEmail,
      captcha
    ]
  );

  const handleClose = useCallback(() => {
    setDialog(false);
  }, [setDialog]);

  const resetResendFlag = useCallback(() => {
    const countdown = c => c - 1;
    const interval = setInterval(() => {
      setCounter(countdown);
    }, 1000);
    setTimer(interval);
  }, []);

  const handleClick = useCallback(async () => {
    if (!canResend) {
      return;
    }

    try {
      setLoading(true);

      if (isAlphanumeric && codeLength === 6) {
        const recaptchaToken = captcha;
        captchaHandlers.reset();

        const response = await ResendPhoneCodeAPI(userEmail, recaptchaToken);

        if (!response.success) {
          setCanResend(true);
          const errors = _get(response, 'error.response.data.errors', {});

          if (errors.recaptcha) {
            setAlert({
              type: 'error',
              message: 'El captcha no es válido'
            });
            return;
          }

          setAlert({
            type: 'error',
            message:
              extractErrorMessage(response).message ||
              'Error al reenviar el código'
          });
          return;
        }

        setCounter(RESEND_FLAG_SECONDS_DELAY);
        setCanResend(false);
        resetResendFlag();
        setAlert({
          type: 'success',
          message: 'Código reenviado exitosamente',
          duration: 2000
        });
        return;
      }

      const response = await ResendPhoneConfirmationAPI(authToken, otpChannels);

      if (!response.success) {
        setCanResend(true);
        setAlert({
          type: 'error',
          message:
            extractErrorMessage(response).message ||
            'Error al reenviar el código'
        });
        return;
      }

      setCounter(RESEND_FLAG_SECONDS_DELAY);
      setCanResend(false);
      resetResendFlag();
      setAlert({
        type: 'success',
        message: 'Código reenviado exitosamente',
        duration: 2000
      });
    } catch (error) {
      setCanResend(true);
      setAlert({
        type: 'error',
        message: 'Error al procesar la solicitud'
      });
    } finally {
      setLoading(false);
    }
  }, [
    authToken,
    canResend,
    setAlert,
    resetResendFlag,
    isAlphanumeric,
    codeLength,
    userEmail,
    captcha,
    captchaHandlers,
    otpChannels
  ]);

  const onChangeCode = useCallback(
    e => {
      const { attributes, value } = e.target;
      const index = parseInt(attributes['data-id'].value, 10);

      if (!isAlphanumeric) {
        setCode(c => {
          const values = [...c];
          values[index] = value;
          return values;
        });
        if (!value) {
          return;
        }
        const nextIndex = Math.min(index + 1, codeLength - 1);
        codeInputs.current[nextIndex] && codeInputs.current[nextIndex].focus();

        return;
      }

      const sanitizedValue = value.slice(-1);
      if (sanitizedValue && !/^[A-Za-z0-9]$/.test(sanitizedValue)) {
        return;
      }
      setCode(c => {
        const values = [...c];
        values[index] = sanitizedValue;
        return values;
      });
      if (!sanitizedValue) {
        return;
      }
      const nextIndex = Math.min(index + 1, codeLength - 1);
      codeInputs.current[nextIndex] && codeInputs.current[nextIndex].focus();
    },
    [codeInputs, isAlphanumeric, codeLength]
  );

  const handlePaste = useCallback(
    e => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').trim();

      if (isAlphanumeric) {
        const regex = new RegExp(`^[A-Za-z0-9]{${codeLength}}$`);
        if (!regex.test(pastedData)) {
          return;
        }

        const pastedArray = pastedData.split('');
        setCode(pastedArray);

        codeInputs.current.forEach((input, index) => {
          if (input) {
            input.value = pastedArray[index];
          }
        });
      } else {
        const regex = new RegExp(`^[0-9]{${codeLength}}$`);
        if (!regex.test(pastedData)) {
          return;
        }

        const pastedArray = pastedData.split('');
        setCode(pastedArray);

        codeInputs.current.forEach((input, index) => {
          if (input) {
            input.value = pastedArray[index];
          }
        });
      }
    },
    [setCode, isAlphanumeric, codeLength]
  );

  useEffect(() => {
    return () => clearInterval(timer);
  }, [timer]);

  useEffect(() => {
    if (counter <= 0) {
      clearInterval(timer);
      setTimer(null);
      setCanResend(true);
    }
  }, [counter, timer]);

  useEffect(() => {
    resetResendFlag();
  }, [resetResendFlag]);

  useEffect(() => {
    if (!code) {
      setCanSubmit(false);
      return;
    }

    try {
      verificationCodeSchema.validateSync({ code });
      setCanSubmit(true);
    } catch (err) {
      setCanSubmit(false);
    }
  }, [code, verificationCodeSchema]);

  const resendText = useMemo(() => {
    if (counter > 0) {
      const minutes = Math.floor(counter / 60);
      let seconds = counter - minutes * 60;
      if (seconds < 10) {
        seconds = `0${seconds}`;
      }
      return `${minutes}:${seconds}`;
    }
    return 'Reenviar código';
  }, [counter]);
  const isMobileSize = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const getMinWidth = useCallback(() => {
    if (isMobileSize) {
      return '100%';
    }
    return codeLength === 6 ? '500px' : '400px';
  }, [isMobileSize, codeLength]);

  const getContentSize = useCallback(() => {
    if (isMobileSize) {
      return 'full';
    }
    if (codeLength === 6) {
      return 'medium';
    }
    return 'small';
  }, [isMobileSize, codeLength]);

  // Specifies the delivery channel of the OTP based on the requested
  // channels, mirroring the backend WhatsApp-first rule (WhatsApp implies
  // SMS, not the other way around). Falls back to the generic message when
  // no channels are requested.
  const renderChannelMessage = () => {
    if (otpChannels && otpChannels.whatsapp && PortalCheck.isGascaribe) {
      return ' a tu celular por WhatsApp ';
    }

    if (otpChannels && (otpChannels.sms || otpChannels.whatsapp)) {
      return ' a tu celular por SMS ';
    }

    return ' a tu celular ';
  };

  const renderMessage = () => {
    if (isAlphanumeric && codeLength === 6) {
      return (
        <>
          {' al correo '}
          <span className={classes.semiBold}>{userEmail}</span>
          {' y a su número de teléfono, en caso de que lo hayas registrado.'}
        </>
      );
    }

    if (phone && phoneCountryCode) {
      return (
        <>
          {renderChannelMessage()}
          <span className={classes.semiBold}>
            (+{phoneCountryCode}) {phone}
          </span>
          {'.'}
        </>
      );
    }

    return '.';
  };

  const renderActions = () => {
    return (
      <>
        <BaseButton
          id="PhoneVerificationDialog_button_cancel"
          onClick={handleClose}
          variant="outlined"
          color="primary"
          size="small"
        >
          Cancelar
        </BaseButton>
        <BaseButton
          onClick={confirmPhone}
          id="PhoneVerificationDialog_button_confirm"
          type="submit"
          form="phoneVerificationForm"
          color="primary"
          size="small"
          autoFocus
          disabled={!canSubmit || loading}
        >
          Confirmar
        </BaseButton>
      </>
    );
  };

  const renderContent = () => {
    return (
      <div className={classes.content}>
        <Typography className={classes.text} variant="caption">
          Por favor, ingrese el código de verificación
          {isAlphanumeric ? ' alfanumérico ' : ' '}
          de {codeLength} {isAlphanumeric ? 'caracteres' : 'dígitos'} que hemos
          enviado
          {renderMessage()}
        </Typography>
        <div className={classes.codeContainer}>
          <form
            id="phoneVerificationForm"
            onSubmit={confirmPhone}
            onPaste={handlePaste}
          >
            {[...Array(codeLength)].map((_, index) => (
              <TextInput
                key={index}
                type={isAlphanumeric ? 'text' : 'number'}
                className={classes.codeInput}
                id={`PhoneVerificationDialog_input_${index + 1}`}
                autoFocus={index === 0}
                placeholder="-"
                margin="none"
                onChange={onChangeCode}
                InputProps={{
                  inputProps: {
                    'data-id': index.toString(),
                    maxLength: 1,
                    min: isAlphanumeric ? undefined : 0,
                    max: isAlphanumeric ? undefined : 9,
                    ref: element => {
                      codeInputs.current[index] = element;
                    }
                  },
                  classes: { input: classes.codeLabel }
                }}
              />
            ))}
          </form>
        </div>
        <span className={classes.codeError}></span>
        {isAlphanumeric && codeLength === 6 && (
          <div ref={inputRef} className={classes.recaptchaContainer}>
            <Recaptcha
              captchaRef={recaptchaRef}
              referenceEl={inputRef}
              heightScale={0.9}
              locale="es"
              onResolved={captchaHandlers.onResolved}
              onExpired={captchaHandlers.onExpired}
              onError={captchaHandlers.onError}
              onLoaded={captchaHandlers.onLoaded}
            />
          </div>
        )}
        {!canResend && (
          <Typography className={classes.subText}>
            Si el mensaje no te ha llegado en 2 minutos, puedes reenviarlo y se
            te generará otro código de verificación.
          </Typography>
        )}
        <div className={classes.actionContainer}>
          <span
            id="PhoneVerificationDialog_span_resendVerification"
            onClick={handleClick}
            className={classes.codeAction}
          >
            {resendText}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <BaseDialog
        id="PhoneVerificationDialog_div"
        open={open}
        disableBackdropClick
        handleClose={handleClose}
        title={title}
        actions={renderActions}
        content={renderContent}
        fullScreen={isMobileSize}
        TransitionComponent={isMobileSize ? SlideUpTransition : undefined}
        contentSize={getContentSize()}
        PaperProps={{
          style: {
            margin: isMobileSize ? 0 : '16px',
            maxHeight: isMobileSize ? '100%' : 'calc(100% - 32px)',
            borderRadius: isMobileSize ? 0 : '8px',
            minWidth: getMinWidth(),
            height: isMobileSize ? '100%' : 'auto'
          }
        }}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4),
      height: '100%',
      justifyContent: 'flex-start',
      maxHeight: '100vh'
    }
  },
  passwordField: {
    width: 250,
    marginTop: theme.spacing(4)
  },
  codeContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: [[theme.spacing(3), 0]],
    flexWrap: 'nowrap',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      width: '100%',
      justifyContent: 'space-around'
    }
  },
  codeInput: {
    maxWidth: props => (props.codeLength === 6 ? 52 : 64),
    width: props => (props.codeLength === 6 ? 52 : 64),
    height: props => (props.codeLength === 6 ? 52 : 64),
    margin: 0,
    '& .MuiInputBase-root': {
      height: '100%'
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: props => (props.codeLength === 6 ? 56 : 70),
      width: props => (props.codeLength === 6 ? 56 : 70),
      height: props => (props.codeLength === 6 ? 56 : 70),
      margin: [[0, theme.spacing(0.5)]]
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '48px',
      width: '48px',
      height: '48px'
    }
  },
  codeLabel: {
    fontSize: 24,
    fontWeight: 500,
    textAlign: 'center',
    padding: '14px 0',
    height: '100%',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '-moz-appearance': 'textfield'
  },
  actionContainer: {
    textAlign: 'center',
    width: '100%',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: 'auto',
      marginBottom: theme.spacing(4)
    }
  },
  codeAction: {
    fontSize: 16,
    color: theme.palette.primary.dark,
    fontWeight: 500,
    cursor: 'pointer',
    margin: '1em auto'
  },
  codeError: {
    color: theme.palette.error.dark
  },
  text: {
    fontSize: 14,
    color: theme.palette.text.primary,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      marginTop: theme.spacing(4)
    }
  },
  subText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    textAlign: 'center',
    marginTop: theme.spacing()
  },
  semiBold: {
    fontWeight: 500
  },
  recaptchaContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2)
    }
  }
}));

export default PhoneVerificationDialog;
